import { useCallback, useEffect, useRef, useState } from 'react';

/** Step size (in px) for keyboard-driven resize via ArrowLeft/ArrowRight. */
const KEYBOARD_STEP = 16;

/** Large step size (in px) used when Shift is held with ArrowLeft/ArrowRight. */
const KEYBOARD_STEP_LARGE = 64;

export interface UseResizableOptions {
  /**
   * Starting width in pixels. Read once at mount — the hook is uncontrolled,
   * so changing this value after mount will NOT reset the width.
   * Clamped to [minWidth, maxWidth] on initialization.
   */
  initialWidth: number;
  /** Minimum allowed width in pixels. */
  minWidth: number;
  /** Maximum allowed width in pixels. */
  maxWidth: number;
  /**
   * Optional ref to the container whose left edge is used as the origin for
   * width calculations. If omitted, the hook falls back to the resize handle's
   * parentElement.getBoundingClientRect().left, which works when the handle is a
   * direct child of the resizable panel (the common case in AppShell's navWrapper).
   */
  containerRef?: React.RefObject<HTMLElement | null>;
}

/** Props to spread onto the resize-handle element (pointer + keyboard handlers). */
export interface ResizeHandleProps {
  onPointerDown: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerMove: (e: React.PointerEvent<HTMLElement>) => void;
  onPointerUp: (e: React.PointerEvent<HTMLElement>) => void;
  onLostPointerCapture: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
}

export interface UseResizableReturn {
  /** Current panel width in pixels. */
  width: number;
  /** Event handler props to spread onto the resize-handle element. */
  handleProps: ResizeHandleProps;
}

/**
 * Reusable hook that provides Pointer-Event-based horizontal resize behaviour.
 *
 * Uses `setPointerCapture` so that all pointer events are routed to the handle
 * element during a drag — no document-level listeners needed. Also adds keyboard
 * accessibility (ArrowLeft / ArrowRight to nudge the width).
 */
export function useResizable(options: UseResizableOptions): UseResizableReturn {
  const { initialWidth, minWidth, maxWidth, containerRef } = options;

  const [width, setWidth] = useState(() =>
    Math.min(maxWidth, Math.max(minWidth, initialWidth)),
  );
  const isDragging = useRef(false);

  /**
   * Left-edge offset of the resizable container, captured at pointer-down time.
   * Width is computed as `clientX - panelLeft` during the drag so that the hook
   * works correctly even when the panel is not positioned at viewport x = 0
   * (e.g. when a global sidebar or header pushes it inward).
   */
  const panelLeftRef = useRef(0);

  // --- Safety: reset body styles on unmount in case user unmounts mid-drag ---
  useEffect(() => {
    return () => {
      if (isDragging.current) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, []);

  const clamp = useCallback(
    (value: number) => Math.min(maxWidth, Math.max(minWidth, value)),
    [minWidth, maxWidth],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      isDragging.current = true;

      // Capture the panel's left offset so we can compute relative width.
      // Prefer the explicitly-passed containerRef; fall back to parentElement.
      const container = containerRef?.current ?? e.currentTarget.parentElement;
      panelLeftRef.current = container
        ? container.getBoundingClientRect().left
        : 0;

      e.currentTarget.setPointerCapture(e.pointerId);
      e.currentTarget.focus();
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [containerRef],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLElement>) => {
      if (!isDragging.current) return;
      const newWidth = clamp(e.clientX - panelLeftRef.current);
      setWidth(newWidth);
    },
    [clamp],
  );

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLElement>) => {
    if (!isDragging.current) return;
    isDragging.current = false;
    e.currentTarget.releasePointerCapture(e.pointerId);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  /**
   * Safety handler: if the browser revokes pointer capture (e.g. alert dialog,
   * iframe stealing focus, touch cancel), pointerUp never fires. This resets
   * drag state so the cursor doesn't stay stuck as col-resize.
   */
  const onLostPointerCapture = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        const step = e.shiftKey ? KEYBOARD_STEP_LARGE : KEYBOARD_STEP;
        setWidth((prev) => clamp(prev + step));
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const step = e.shiftKey ? KEYBOARD_STEP_LARGE : KEYBOARD_STEP;
        setWidth((prev) => clamp(prev - step));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setWidth(minWidth);
      } else if (e.key === 'End') {
        e.preventDefault();
        setWidth(maxWidth);
      }
    },
    [clamp, minWidth, maxWidth],
  );

  return {
    width,
    handleProps: {
      onPointerDown,
      onPointerMove,
      onPointerUp,
      onLostPointerCapture,
      onKeyDown,
    },
  };
}
